import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
    Body1,
    Body2,
    Box,
    Flex,
    IconChevronRight,
    Subtitle2,
    TextButton,
} from '@myphx-engineering/phx-design-system';
import { MedFormIcon } from '@phx/myphx-lib';

import DrugDetails from '../../../components/common/DrugDetails';
import { getFragment } from '../../../graphql';
import {
    PrescriberOrderStatus,
    RenewalRequestStatus,
} from '../../../graphql/generated/graphql';
import { useFlags } from '../../../hooks/use-flags';
import { usePriorAuthorizationDetails } from '../../../hooks/use-prior-authorization-details';
import {
    formatToLocalizedDate,
    getAbsoluteRoute,
    titleCase,
} from '../../../util';
import {
    getLatestActionDate,
    getMedicationDescription,
    getOrderQuantity,
    getPrescribedProductFragment,
} from '../../../util/cabinet/prescriber-orders';

import type { Prescription } from './HistorySearch';

type HistorySearchItemProps = {
    item: Prescription;
};

export const HistorySearchItem = ({ item }: HistorySearchItemProps) => {
    const { drugDetailsSplitEnabled, enableRxCancelMyphx } = useFlags();
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { isNonDrxPriorAuthorization } = usePriorAuthorizationDetails(item);

    const product = getFragment(getPrescribedProductFragment(item));
    const description = getMedicationDescription(item);
    const patient = getFragment(item.patient);
    const form =
        product?.__typename === 'Product'
            ? product?.formValue
            : product?.formCode;

    const isDigitallyReceived =
        item?.__typename === 'DigitallyReceivedPrescriberOrder';

    const cancelledPrescriberOrder =
        enableRxCancelMyphx &&
        isDigitallyReceived &&
        item.status === PrescriberOrderStatus.Cancelled;
    const pendingPrescriberOrder =
        enableRxCancelMyphx &&
        isDigitallyReceived &&
        item.renewalRequestDetails?.state === RenewalRequestStatus.Sent;

    const route =
        isDigitallyReceived && item.drxId
            ? getAbsoluteRoute('orders.prescriberOrderId.details', {
                  prescriberOrderId: item.drxId,
              })
            : isNonDrxPriorAuthorization && item.id
              ? getAbsoluteRoute(
                    'priorAuthorization.priorAuthorizationId.details',
                    {
                        priorAuthorizationId: item.id,
                    }
                )
              : '';
    const linkTo =
        (isDigitallyReceived && item.drxId) ||
        (isNonDrxPriorAuthorization && item.id)
            ? () => navigate(route, { state: { prev: 'history' } })
            : undefined;

    const rawDate = getLatestActionDate(item);
    const date = rawDate
        ? formatToLocalizedDate(
              new Date(rawDate),
              { dateStyle: 'medium' },
              i18n.language
          )
        : null;

    const controlledSubstanceCode = isDigitallyReceived
        ? getFragment(item.authorizedMedication).controlledSubstanceCode
        : null;

    // Temporary until VELA integration
    const isInstantRouting = false;

    const displayProductNameOnly =
        drugDetailsSplitEnabled ||
        (!isInstantRouting && controlledSubstanceCode);

    return (
        <Flex
            gap="xs"
            justify="space-between"
            align="start"
            data-testid="prescription-history-block"
            onClick={enableRxCancelMyphx ? undefined : linkTo}
            style={
                enableRxCancelMyphx
                    ? undefined
                    : linkTo
                      ? { cursor: 'pointer', userSelect: 'none' }
                      : undefined
            }
        >
            <Flex gap="xs">
                <MedFormIcon form={form} />
                <Box>
                    <Subtitle2 data-testid="prescription-history-block-drug-description">
                        {displayProductNameOnly
                            ? titleCase(product?.name)
                            : description}
                    </Subtitle2>
                    {drugDetailsSplitEnabled && product ? (
                        <Box style={{ marginLeft: '10px' }}>
                            <DrugDetails
                                product={product}
                                quantity={getOrderQuantity(item)}
                            />
                        </Box>
                    ) : null}
                    {cancelledPrescriberOrder ? (
                        <Body1>{t('cabinet.history.cancelled')}</Body1>
                    ) : null}
                    <Body1 data-testid="prescription-history-block-patient">
                        {t('cabinet.medicationOrder.forName', {
                            name: patient.fullName,
                        })}
                    </Body1>
                    {pendingPrescriberOrder ? (
                        <Body1>{t('cabinet.history.pendingApproval')}</Body1>
                    ) : linkTo ? (
                        <TextButton
                            RightIcon={IconChevronRight}
                            onClick={linkTo}
                            data-testid="prescription-history-block-view-order-button"
                        >
                            {t('orderDetails.viewDetails')}
                        </TextButton>
                    ) : null}
                </Box>
            </Flex>
            {date ? <Body2 noWrap>{date}</Body2> : null}
        </Flex>
    );
};
